// Members of a department. Every call goes through row-level security, so a
// treasurer only ever gets their own department's people back.
import { supabase } from '@/lib/supabase'
import { currentUserId } from './auth'
import { DataError, unwrap } from './errors'

export interface DepartmentMember {
  membershipId: string
  memberId: string
  fullName: string
  phone: string | null
  email: string | null
  notes: string | null
  joinedOn: string
  /** null = currently in the department. */
  leftOn: string | null
  /** Money overdue, in ngwee (0 if up to date). */
  arrearsNgwee: number
  periodsBehind: number
  oldestUnpaidPeriod: string | null
}

type MembershipJoin = {
  id: string
  member_id: string
  joined_on: string
  left_on: string | null
  member: { id: string; full_name: string; phone: string | null; email: string | null; notes: string | null } | null
}

type ArrearsRow = {
  member_id: string
  arrears_ngwee: number
  periods_behind: number
  oldest_unpaid_period: string | null
}

/** Everyone who has been in the department (current and past), with what they owe. */
export async function listDepartmentMembers(departmentId: string): Promise<DepartmentMember[]> {
  const [people, arrears] = await Promise.all([
    supabase
      .from('department_membership')
      .select('id, member_id, joined_on, left_on, member:member_id(id, full_name, phone, email, notes)')
      .eq('department_id', departmentId),
    supabase
      .from('v_member_arrears')
      .select('member_id, arrears_ngwee, periods_behind, oldest_unpaid_period')
      .eq('department_id', departmentId),
  ])
  const rows = unwrap(people as never, 'Could not load the members.') as MembershipJoin[]
  const owed = new Map(
    (unwrap(arrears as never, 'Could not load the members.') as ArrearsRow[]).map((r) => [r.member_id, r]),
  )

  // A person who left and rejoined has two membership rows. Show them once,
  // preferring the current membership, otherwise the most recent one.
  const best = new Map<string, MembershipJoin>()
  for (const r of rows ?? []) {
    if (!r.member) continue
    const seen = best.get(r.member_id)
    const better =
      !seen ||
      (seen.left_on !== null && r.left_on === null) ||
      (seen.left_on !== null && r.left_on !== null && r.joined_on > seen.joined_on)
    if (better) best.set(r.member_id, r)
  }

  return [...best.values()]
    .map((r) => {
      const o = owed.get(r.member_id)
      return {
        membershipId: r.id,
        memberId: r.member_id,
        fullName: r.member!.full_name,
        phone: r.member!.phone,
        email: r.member!.email,
        notes: r.member!.notes,
        joinedOn: r.joined_on,
        leftOn: r.left_on,
        arrearsNgwee: Number(o?.arrears_ngwee ?? 0),
        periodsBehind: Number(o?.periods_behind ?? 0),
        oldestUnpaidPeriod: o?.oldest_unpaid_period ?? null,
      }
    })
    .sort((a, b) => a.fullName.localeCompare(b.fullName))
}

export interface NewMember {
  departmentId: string
  fullName: string
  /** Canonical +260XXXXXXXXX (see normalizeZambianPhone) or null. */
  phone: string | null
  email?: string | null
  notes?: string | null
  joinedOn: string
}

export type AddMemberResult =
  | { kind: 'created'; memberId: string; fullName: string }
  /** Someone already in the church (another department) was added here. */
  | { kind: 'added-existing'; memberId: string; fullName: string }
  | { kind: 'already-member'; memberId: string; fullName: string }

type FoundMember = { id: string; full_name: string; in_my_department: boolean }

/**
 * Add a person to a department WITHOUT creating a duplicate.
 *
 * Members are matched by phone number across the whole church. If the number is
 * already known, the existing person is added to this department (their payment
 * history stays in one place). Otherwise a new person is created.
 */
export async function addMemberToDepartment(input: NewMember): Promise<AddMemberResult> {
  const userId = await currentUserId()

  if (input.phone) {
    const found = await supabase.rpc('find_member_by_phone', { p_phone: input.phone })
    const matches = unwrap(found as never, 'Could not check whether this person already exists.') as FoundMember[]
    const existing = matches?.[0]
    if (existing) {
      if (existing.in_my_department) {
        const current = await supabase
          .from('department_membership')
          .select('id')
          .eq('department_id', input.departmentId)
          .eq('member_id', existing.id)
          .is('left_on', null)
          .maybeSingle()
        if (current.data) {
          return { kind: 'already-member', memberId: existing.id, fullName: existing.full_name }
        }
      }
      await insertMembership(userId, input.departmentId, existing.id, input.joinedOn)
      return { kind: 'added-existing', memberId: existing.id, fullName: existing.full_name }
    }
  }

  const created = await supabase
    .from('member')
    .insert({
      full_name: input.fullName.trim(),
      phone: input.phone,
      email: input.email?.trim() || null,
      notes: input.notes?.trim() || null,
      created_by: userId,
    })
    .select('id, full_name')
    .single()
  const member = unwrap(created as never, 'Could not save the member.') as { id: string; full_name: string }
  await insertMembership(userId, input.departmentId, member.id, input.joinedOn)
  return { kind: 'created', memberId: member.id, fullName: member.full_name }
}

async function insertMembership(userId: string, departmentId: string, memberId: string, joinedOn: string) {
  const res = await supabase.from('department_membership').insert({
    member_id: memberId,
    department_id: departmentId,
    joined_on: joinedOn,
    created_by: userId,
  })
  if (res.error) throw new DataError(res.error, 'Could not add the member to the department.')
}

export interface MemberEdit {
  fullName: string
  phone: string | null
  email: string | null
  notes: string | null
}

export async function updateMember(memberId: string, edit: MemberEdit): Promise<void> {
  const res = await supabase
    .from('member')
    .update({
      full_name: edit.fullName.trim(),
      phone: edit.phone,
      email: edit.email?.trim() || null,
      notes: edit.notes?.trim() || null,
    })
    .eq('id', memberId)
  if (res.error) throw new DataError(res.error, 'Could not save the changes.')
}

/**
 * Take someone out of a department. Their history stays; they stop being billed.
 * Anything they already owe stays on record until the treasurer waives it.
 */
export async function removeMemberFromDepartment(
  membershipId: string,
  departmentId: string,
  leftOn: string,
): Promise<void> {
  const ended = await supabase
    .from('dues_enrollment')
    .update({ end_on: leftOn })
    .eq('membership_id', membershipId)
    .eq('department_id', departmentId)
    .is('end_on', null)
  if (ended.error) throw new DataError(ended.error, 'Could not stop their dues.')

  const res = await supabase
    .from('department_membership')
    .update({ left_on: leftOn })
    .eq('id', membershipId)
    .eq('department_id', departmentId)
  if (res.error) throw new DataError(res.error, 'Could not remove the member.')
}
